import { ICasinoGame, IGetCasinoGamesRequest } from "@finbackoffice/clientbff-client";
import { CasinoGameTag } from "@finbackoffice/enums";
import { RequestError, CasinoGameTypeEnum } from "@finbackoffice/fe-core";
import { ICollection } from "@finbackoffice/http-client";
import { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ClientBFFContext } from "../providers";
import { PaginationType, DEFAULT_PAGINATION } from "../types";
import { usePrevious } from "./usePrevious";

export enum CasinoScopeEnum {
    LIVE_CASINO = "livecasino",
    SLOTS = "slots",
}

export const CasinoScopesMap: Record<CasinoScopeEnum, CasinoGameTypeEnum[]> = {
    [CasinoScopeEnum.SLOTS]: [
        CasinoGameTypeEnum.VIDEO_SLOTS,
        CasinoGameTypeEnum.TABLE_GAMES,
        CasinoGameTypeEnum.FAST_GAMES,
        CasinoGameTypeEnum.CRASH_GAMES,
        CasinoGameTypeEnum.VIDEO_BINGO,
        CasinoGameTypeEnum.VIDEO_POKER,
        CasinoGameTypeEnum.SCRATCH_CARDS,
        CasinoGameTypeEnum.VIRTUAL_GAMES,
        CasinoGameTypeEnum.VIRTUAL_SPORTS,
        CasinoGameTypeEnum.LIVE_GAMES,
    ],
    [CasinoScopeEnum.LIVE_CASINO]: [
        CasinoGameTypeEnum.LIVE_CASINO,
        CasinoGameTypeEnum.LIVE_CASINO_V2,
        CasinoGameTypeEnum.LIVE_CASINO_TABLE,
    ],
};

export type ICasinoDataState = {
    games: ICasinoGame[];
    totalCount: number;
    loading: boolean;
};

export type UseCasinoGamesHookOptions = {
    megaways?: boolean;
    bonusbuy?: boolean;
    has_jackpot?: boolean;
    gameName?: string;
    gameId?: string;
    defaultPagination?: Partial<PaginationType>;
    mobile?: boolean;
    onError?: (error: RequestError) => void;
};

export const useCasinoGames = (
    gameTypes: CasinoGameTypeEnum[],
    tag: CasinoGameTag | "",
    provider: string,
    options?: UseCasinoGamesHookOptions,
) => {
    const { t } = useTranslation();
    const {
        megaways,
        bonusbuy,
        has_jackpot,
        gameName,
        gameId,
        defaultPagination,
        mobile,
        onError,
    } = options ?? {};
    const client = useContext(ClientBFFContext);

    const [casinoGamesState, setCasinoGamesState] = useState<ICasinoDataState>({
        games: [],
        totalCount: 0,
        loading: true,
    });
    const [pagination, setPagination] = useState<PaginationType>({
        ...DEFAULT_PAGINATION,
        ...defaultPagination,
    });

    const prevGameName = usePrevious(gameName);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        let isMounted = true;
        const loadData = async () => {
            const params: IGetCasinoGamesRequest = {
                page: pagination.page,
                items_per_page: pagination.items_per_page,
                mobile: !!mobile,
                megaways: !!megaways,
                bonusbuy: !!bonusbuy,
                has_jackpot: !!has_jackpot,
                types: gameTypes.map((type) => type.toString()),
                tag: tag || undefined,
                category: provider === "All" ? undefined : provider,
                name: gameName,
                id: gameId,
            };

            setCasinoGamesState((state) => ({
                ...state,
                loading: true,
            }));
            try {
                const response: ICollection<ICasinoGame> = await client.getCasinoGames(params);

                if (isMounted) {
                    if (!response.items.length) {
                        onError?.({
                            error: "info",
                            message: t("no_games"),
                        });
                    }

                    setCasinoGamesState((state) => ({
                        ...state,
                        games: [...state.games, ...response.items],
                        totalCount: response.total_count,
                        loading: false,
                    }));
                }
            } catch (err: any) {
                const error: RequestError = err.response?.data;

                if (isMounted) {
                    onError?.(error);
                    setCasinoGamesState((state) => ({
                        ...state,
                        loading: false,
                    }));
                }
            }
        };

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        if (gameName || gameId || gameTypes.length || tag) {
            timeoutRef.current = setTimeout(
                () => {
                    loadData();
                },
                prevGameName !== gameName ? 500 : 0,
            );
        }

        return () => {
            isMounted = false;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        gameTypes,
        tag,
        gameName,
        gameId,
        provider,
        pagination.page,
        pagination.items_per_page,
        megaways,
        bonusbuy,
        has_jackpot,
    ]);

    useEffect(() => {
        if (pagination.page !== 1) {
            const newValue = DEFAULT_PAGINATION;

            if (defaultPagination?.items_per_page) {
                newValue.items_per_page = defaultPagination.items_per_page;
            }
            setPagination(newValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tag, gameTypes, provider, megaways, bonusbuy, has_jackpot]);

    useEffect(() => {
        setCasinoGamesState((state) => ({
            ...state,
            games: [],
        }));
    }, [gameTypes, tag, gameName, gameId, provider, megaways, bonusbuy, has_jackpot]);

    return {
        casinoGamesState,
        pagination,
        setPagination,
        hasMore: casinoGamesState.games.length !== casinoGamesState.totalCount,
    };
};
